import { useEffect, useMemo, useState } from "react";
import { CopyTaskParametersDc, PipelineTaskParametersDc } from "@evergis/api";

import { useProgress } from "../../../../hooks/scheduler/useProgress";
import { collectErrorLog } from "../../../../utils/collectErrorLog";
import { copyTask, pipelineTask } from "../../../../resources/scheduler";
import { createPipelineTask } from "../../../../evergis/core/tools/createPipelineTask";

import {
  AnyTaskParameters,
  CsvStaticTaskDataStorage,
  ExcelStaticTaskDataStorage,
  LayerTaskDataStorage,
  SchedulerTask,
  StaticTaskDataStorage,
  TaskDataType,
} from "../../../../interfaces/scheduler";

import { SpatialReferenceData } from "./useSpatialReference";
import { ImportAttribute, ImportErrorState, ImportState, ImportType } from "../types";

export type ImportPollingParams = {
  fileId: string;
  fileType: string;
  sourceName: string | string[];
  targetLayerName: string;
  srData?: SpatialReferenceData;
  attributes?: ImportAttribute[];
};

export const useImportPolling = (type: ImportType, params: ImportPollingParams): [ImportState, ImportErrorState] => {
  const { fileId, fileType, sourceName, targetLayerName, srData, attributes } = params;
  const [runCopyTask, { data: taskResult }] = (type === ImportType.KML ? pipelineTask : copyTask).use();
  const [importInProgress, setInProgress] = useState(false);
  const { processed, isTaskCompleted, result } = useProgress(taskResult && taskResult.taskId);
  const [error, setError] = useState<string>();
  const [errorsCount, setErrorsCount] = useState(0);
  const [errorLog, setErrorLog] = useState<string>();

  const importTaskParams = useMemo(() => {
    if (Array.isArray(sourceName)) {
      return createPipelineTask(
        sourceName.map(sourceNameItem =>
          getImportTaskParams(
            fileId,
            fileType,
            sourceNameItem,
            targetLayerName,
            srData,
            attributes,
            SchedulerTask.Copy,
          ),
        ) as AnyTaskParameters[],
        true,
      );
    }

    return getImportTaskParams(fileId, fileType, sourceName, targetLayerName, srData, attributes);
  }, [attributes, fileId, fileType, sourceName, srData, targetLayerName]);

  useEffect(() => {
    if (!targetLayerName) {
      return;
    }

    runCopyTask({ input: importTaskParams as PipelineTaskParametersDc & CopyTaskParametersDc });
    setInProgress(true);
  }, [targetLayerName]);

  useEffect(() => {
    if (isTaskCompleted) {
      setInProgress(false);
    }
  }, [isTaskCompleted]);

  useEffect(() => {
    if (result?.stepResults) {
      const { errorCount, message } = result;
      const log = collectErrorLog(result.stepResults);

      message && setError(message);
      errorCount && setErrorsCount(errorCount);
      log && setErrorLog(log);
    }
  }, [result]);

  return [
    { importInProgress, processed, importDone: isTaskCompleted, taskId: taskResult && taskResult.taskId },
    { message: error, count: errorsCount, log: errorLog },
  ];
};

declare type ImportCopyTaskParams = {
  source: StaticTaskDataStorage | ExcelStaticTaskDataStorage | CsvStaticTaskDataStorage;
  target: LayerTaskDataStorage;
  attributeMapping?: Record<string, string>;
  type?: SchedulerTask;
};

function getTaskType(ext: string): TaskDataType {
  if (["kml", "kmz"].includes(ext)) {
    return TaskDataType.Kml;
  }

  if (ext === "csv") {
    return TaskDataType.Csv;
  }

  if (ext === "xlsx") {
    return TaskDataType.Excel;
  }

  if (ext === "gdb") {
    return TaskDataType.Gdb;
  }

  return TaskDataType.Static;
}

// eslint-disable-next-line max-lines-per-function
export function getImportTaskParams(
  fileId: string,
  fileType: string,
  sourceName: string,
  targetLayerName: string,
  srData?: SpatialReferenceData,
  attributes?: ImportAttribute[],
  taskType?: SchedulerTask,
): ImportCopyTaskParams {
  const importCopyTaskParams = {
    source: {
      type: getTaskType(fileType),
      fileName: fileId,
      layerName: sourceName,
      ...(srData
        ? {
            spatialReference: srData.srFields?.srid,
            aliasRowNumber: -1,
            attributeNameRowNumber: 0,
            firstDataRowNumber: 1,
            coordSourceFields: srData.lonLatInOneField
              ? [srData.srFields?.longitudeAndLatitude]
              : [srData.srFields?.longitude, srData.srFields?.latitude],
          }
        : {}),
    },
    target: {
      type: TaskDataType.Layer,
      serviceName: targetLayerName,
      createNewService: false,
    },
    attributeMapping: attributes
      ? attributes.reduce(
          (acc, { name, originalName }) => ({
            ...acc,
            [originalName]: name,
          }),
          {},
        )
      : void 0,
  };

  return taskType
    ? {
        type: taskType,
        ...importCopyTaskParams,
      }
    : importCopyTaskParams;
}
